import { useSyncExternalStore } from 'react';
import { onlineManager } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { WifiOff } from 'lucide-react';

/**
 * Says the machine is offline, because otherwise nothing does.
 *
 * TanStack Query's default `networkMode: 'online'` **pauses** queries while the
 * browser reports no connection rather than failing them. That is the right
 * behaviour — a request fired at a dead network wastes a retry budget and buries
 * the real error — but the screen it produces is a permanent "Ładowanie…" with
 * `isPending` true, `isError` false and no error to render. Every loading state
 * in this app is honest except that one, and it is honest about the wrong thing.
 *
 * Subscribing to `onlineManager` rather than to `window`'s online/offline events
 * directly, so the banner and the query layer can never disagree: it is the same
 * observable Query itself pauses on. Mocking it is also how the E2E test drives
 * this without unplugging anything.
 */
export const OfflineBanner = () => {
  const { t } = useTranslation();
  const online = useSyncExternalStore(
    (callback) => onlineManager.subscribe(callback),
    () => onlineManager.isOnline(),
    // The server snapshot: this app never renders on a server, but the argument
    // is what stops React from calling the client getter during hydration.
    () => true,
  );

  if (online) {
    return null;
  }

  return (
    <div
      role="status"
      className="flex items-center justify-center gap-2 bg-warning-bg px-4 py-1.5 text-xs text-warning-fg"
    >
      <WifiOff aria-hidden className="size-3.5 shrink-0" />
      {t('common.offline')}
    </div>
  );
};
